// ABOUTME: Card component displaying uncelebrated completed goals with server-side pagination
// ABOUTME: Uses filterStatus=completed API param to fetch only relevant goals, sorted oldest first

import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Avatar,
  CircularProgress,
  Badge,
  TablePagination
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Goal, Child } from '../types/api';
import { goalsAPI, familyAPI } from '../services/api';
import CelebrationModal from './CelebrationModal';
import { errorTint } from '../theme/tokens';
import naluV1Theme from '../theme/theme.nalu-v1';

interface GoalsToCelebrateCardProps {
  familyId: number;
}

const GoalsToCelebrateCard: React.FC<GoalsToCelebrateCardProps> = ({ familyId }) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [goals, setGoals] = useState<Goal[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [celebrationModal, setCelebrationModal] = useState<{
    isOpen: boolean;
    goal: Goal | null;
    childName: string;
  }>({ isOpen: false, goal: null, childName: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const hasLoadedOnce = useRef(false);

  // Load children once per family
  useEffect(() => {
    const loadChildren = async () => {
      try {
        const familyChildren = await familyAPI.getChildren(familyId);
        setChildren(familyChildren);
      } catch (error: unknown) {
        console.error('Failed to load children:', error);
      }
    };
    loadChildren();
  }, [familyId]);

  // Reset page and hasLoadedOnce when familyId changes
  const prevFamilyId = useRef(familyId);
  useEffect(() => {
    if (prevFamilyId.current !== familyId) {
      prevFamilyId.current = familyId;
      hasLoadedOnce.current = false;
      setPage(0);
      // page reset to 0 triggers the goal-loading effect below
    }
  }, [familyId]);

  // Load goals with server-side filtering and pagination
  useEffect(() => {
    loadGoals();
  }, [familyId, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoals = async () => {
    try {
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      setError(null);

      const response = await goalsAPI.getFamilyGoalsPaginated(familyId, {
        filterStatus: 'completed',
        page: page + 1, // API uses 1-based pages
        pageSize: rowsPerPage,
        sortBy: 'completedat', // Explicit sort field to override smart default
        sortOrder: 'asc', // Oldest uncelebrated first
      });

      // Handle empty page after celebration — decrement page
      if (response.data.length === 0 && page > 0) {
        setTotalCount(response.totalCount);
        setPage(p => p - 1);
        return;
      }

      setGoals(response.data);
      setTotalCount(response.totalCount);
      hasLoadedOnce.current = true;
    } catch (error: unknown) {
      console.error('Failed to load goals to celebrate:', error);
      setError('Error loading goals');
    } finally {
      setLoading(false);
    }
  };

  const handleCelebrateGoal = (goal: Goal) => {
    const child = children.find(c => (c.childId || c.id) === goal.childId);
    const childName = child?.name || 'Unknown Child';
    setCelebrationModal({ isOpen: true, goal, childName });
  };

  const handleGoalCelebrated = async (_celebratedGoal: Goal) => {
    setCelebrationModal({ isOpen: false, goal: null, childName: '' });
    // Refetch current page from server to get updated data
    loadGoals();
  };

  const getChildById = (childId: number) => {
    return children.find(c => (c.childId || c.id) === childId);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays === 0) {
      if (diffInHours === 0) {
        return 'Today';
      }
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays === 1) {
      return '1 day ago';
    } else {
      return `${diffInDays} days ago`;
    }
  };

  if (!hasLoadedOnce.current && loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>Loading goals...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">Error loading goals to celebrate</Typography>
        </CardContent>
      </Card>
    );
  }

  if (totalCount === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">No goals to celebrate</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card role="region" aria-label="Goals to celebrate">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Emoji glyph sizing via fontSize, not a Typography text override */}
              <Box sx={{ mr: 1, fontSize: '1.5rem' }} role="img" aria-label="party popper">
                🎉
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Goals to Celebrate
              </Typography>
            </Box>
            <Badge
              badgeContent={totalCount}
              color="error"
              sx={{
                mr: 2, // Same right margin as the content below
                '& .MuiBadge-badge': {
                  backgroundColor: theme.palette.error.dark,
                  // nalu-v1's error palette explicitly documents white failing contrast here
                  // (theme.nalu-v1.ts); default theme has no such issue on its saturated red,
                  // so only nalu-v1 switches away from the original literal 'white' (issue #1900).
                  color: isNaluV1Theme ? theme.palette.error.contrastText : 'white',
                  fontWeight: 600
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {goals.map((goal) => {
              const child = getChildById(goal.childId);
              const completionTime = goal.completedAt || goal.updatedAt;

              return (
                <Box
                  key={goal.id}
                  data-testid={`goal-item-${goal.id}`}
                  sx={{
                    backgroundColor: isNaluV1Theme ? alpha(theme.palette.error.main, 0.08) : errorTint[50],
                    borderRadius: isNaluV1Theme ? '24px' : 2,
                    p: 2,
                    border: `1px solid ${alpha(theme.palette.error.dark, 0.1)}`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    {child?.profileImageUrl ? (
                      <Avatar
                        src={child.profileImageUrl}
                        alt={`${child.name}'s avatar`}
                        sx={{ width: 32, height: 32, mr: 2 }}
                      />
                    ) : (
                      // Emoji glyph sizing via fontSize, not a Typography text override
                      <Box sx={{ mr: 2, fontSize: '1.5rem' }}>
                        😊
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {child?.name || 'Unknown Child'} completed: {goal.title}
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: theme.palette.error.dark, ml: 1 }}
                        >
                          {formatTimeAgo(completionTime)}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Reward: {goal.reward || goal.rewardDescription || 'No reward specified'}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => handleCelebrateGoal(goal)}
                        sx={{
                          backgroundColor: theme.palette.success.main,
                          // Default theme's success.contrastText is explicitly black (2.54:1
                          // white contrast documented as failing) — same real issue as nalu-v1's,
                          // but out of scope here to fix for other themes (issue #1900 is nalu-v1
                          // only); nalu-v1 uses its own (also-black) contrastText, others keep white.
                          color: isNaluV1Theme ? theme.palette.success.contrastText : 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600, // Make button text bold
                          py: 0.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: theme.palette.success.dark
                          }
                        }}
                        size="small"
                      >
                        Mark as Celebrated
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {totalCount > rowsPerPage && (
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                data-testid="goals-to-celebrate-pagination"
                aria-label="Goals to celebrate pagination"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={celebrationModal.isOpen}
        goal={celebrationModal.goal}
        childName={celebrationModal.childName}
        onCelebrated={handleGoalCelebrated}
        onClose={() => setCelebrationModal({ isOpen: false, goal: null, childName: '' })}
      />
    </>
  );
};

export default GoalsToCelebrateCard;
